import { ReactNode, useState } from "react";
import {
  ExpandOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { Button, Flex, Tooltip } from "antd";
import { useTranslate } from "@refinedev/core";
import { useTemplatesStatusStore } from "../store/useTemplatesStatusStore";
import {
  ZOOM_MAX_PERCENT,
  ZOOM_MIN_PERCENT,
  ZOOM_STEP_PERCENT,
} from "../constants";

/** Icon button whose tooltip hides on click (antd keeps it open otherwise
 *  because the button stays hovered/focused). */
const ZoomButton = ({
  title,
  icon,
  disabled,
  onClick,
}: {
  title: string;
  icon: ReactNode;
  disabled: boolean;
  onClick: () => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip title={title} open={open} onOpenChange={setOpen}>
      <Button
        type="text"
        shape="round"
        icon={icon}
        disabled={disabled}
        onClick={() => {
          setOpen(false);
          onClick();
        }}
      />
    </Tooltip>
  );
};

/**
 * Scene canvas zoom control for the storyboard header: zoom out, reset to
 * original size (100%), zoom in. Zoom is purely visual — it scales the rendered
 * scene preview only, never the relative item sizes saved in the template.
 */
export const ZoomControl = () => {
  const t = useTranslate();
  const zoom = useTemplatesStatusStore((state) => state.zoom);
  const setZoom = useTemplatesStatusStore((state) => state.setZoom);

  const percent = Math.round(zoom * 100);

  const setPercent = (next: number) =>
    setZoom(Math.min(ZOOM_MAX_PERCENT, Math.max(ZOOM_MIN_PERCENT, next)) / 100);

  return (
    <Flex align="center" gap={10}>
      <ZoomButton
        title={t("buttons.zoomOutScene")}
        icon={<ZoomOutOutlined style={{ fontSize: 18 }} />}
        disabled={percent <= ZOOM_MIN_PERCENT}
        onClick={() => setPercent(percent - ZOOM_STEP_PERCENT)}
      />
      <ZoomButton
        title={t("buttons.zoomOriginalSize")}
        icon={<ExpandOutlined style={{ fontSize: 18 }} />}
        disabled={percent === 100}
        onClick={() => setZoom(1)}
      />
      <ZoomButton
        title={t("buttons.zoomInScene")}
        icon={<ZoomInOutlined style={{ fontSize: 18 }} />}
        disabled={percent >= ZOOM_MAX_PERCENT}
        onClick={() => setPercent(percent + ZOOM_STEP_PERCENT)}
      />
    </Flex>
  );
};
